// Team round generation.
//
// The team-versus-team pairing comes from bbpPairings, the same FIDE-endorsed engine that
// pairs the individual Swiss here (js/services/pairing-engine.js). It is given a projection
// of the tournament in which each TEAM is one player scoring match points — see
// js/services/team-pairing-trf.js for how that file is built and what it can and cannot
// express.
//
// Three things happen on the way back, in this order, and the order is C.04.6's own
// (art. 3.2: pair, then allocate colours, and only then set the boards):
//
//   1. the engine's pairs are mapped from team pairing numbers back to teams;
//   2. the colours it chose are DISCARDED and reassigned under C.04.6 art. 4.3, because
//      the engine applies the individual Dutch colour rules and C.04.6 excludes those from
//      team play entirely;
//   3. each match is expanded onto boards from the two captains' line-ups.
//
// Steps 2 and 3 are TeamEngine's; this module is the round trip and the error handling.

import { runDutch, parsePairingList, describeExit, BbpExit, preload as preloadEngine } from './bbp-wrapper.js';

// Every error thrown here ends up in front of the arbiter as a dialog, so it is built from
// a translatable template — see TeamEngine.text for why a spliced sentence cannot be.
const say = (key, params) => window.TeamEngine.text(key, params);

/**
 * Start fetching the engine ahead of time, so the ~550 KB download happens while the
 * arbiter is still looking at the team list rather than when they ask for pairings.
 */
export function preload() {
    return preloadEngine();
}

/**
 * Generate the next round of team matches, with their boards.
 *
 * @param {Object} opts
 * @param {Array}  opts.teams     [{ id, teamNo, name, squad: [playerId], status }]
 * @param {Array}  opts.rounds    completed rounds
 * @param {Object} opts.settings  { boards, matchPointSystem, pointSystem, primaryScore,
 *                                  colourPrefType, initialColour, roundsCount }
 * @param {Object} [opts.lineups] teamId -> [playerId] for the round being generated;
 *                                a team with no entry fields its top `boards` players
 * @param {Function} [opts.isAvailable] playerId -> boolean, for the default line-up
 * @param {string} opts.name      tournament name, for the TRF header
 * @returns {Promise<{ matches, pairings, warnings, colourRisks }>}
 * @throws {Error} with a message the arbiter can act on
 */
/**
 * Expand a settled list of team pairs onto boards.
 *
 * Shared by both pairing systems, because everything from here down — whose captain
 * fielded whom, which board is which colour — is identical whether the two teams were
 * brought together by a Swiss bracket or by a Berger table.
 */
function buildMatches(coloured, byes, { teams, boards, roundNo, lineups, isAvailable }) {
    const TeamEngine = window.TeamEngine;
    const teamById = new Map(teams.map(t => [t.id, t]));
    const matches = [];
    const pairings = [];

    const lineupFor = (teamId) => {
        const chosen = lineups[teamId];
        if (Array.isArray(chosen) && chosen.some(id => id)) return chosen;
        const squad = (teamById.get(teamId) || {}).squad || [];
        return TeamEngine.defaultLineup(squad, boards, isAvailable);
    };

    coloured.forEach(({ whiteTeam, blackTeam }, i) => {
        // Stable and readable, so a match id survives a reload and can be recognised in
        // exported data: round, then position.
        const id = `r${roundNo}m${i + 1}`;
        const match = { id, roundNo, whiteTeam, blackTeam, boards };
        matches.push(match);
        TeamEngine.expandMatch(match, lineupFor(whiteTeam), lineupFor(blackTeam), boards)
            .forEach(g => pairings.push(g));
    });

    byes.forEach((teamId, i) => {
        matches.push({ id: `r${roundNo}bye${i + 1}`, roundNo, whiteTeam: teamId, blackTeam: 'BYE', boards });
    });

    return { matches, pairings };
}

/**
 * One round of a team ROUND ROBIN, straight off the FIDE Berger table.
 *
 * None of the Swiss machinery applies here and none of it is used: there are no
 * scoregroups, no floats and no colour preferences, because the whole schedule — who
 * meets whom, and which side has White — was fixed before the first move. C.04.6 governs
 * Swiss team pairing; a round robin is decided by the table in Competition Rules
 * Appendix 1, which RoundRobinEngine already implements for individuals.
 *
 * The teams stand in for players in that engine, ordered by Team Pairing Number. That
 * substitution is safe in a way the Swiss one is not: buildBergerTable() only ever reads
 * positions and ids, never scores, ratings or colour history.
 */
function generateTeamBergerRound({ teams, rounds, settings, lineups, isAvailable }) {
    const boards = parseInt(settings.boards, 10) || 4;
    const roundNo = rounds.length + 1;
    const doubled = settings.pairingSystem === 'double-round-robin';

    // Pairing numbers are fixed for the whole competition (C.04.6 art. 2.2) and this app
    // never reuses one, so the Berger field is simply the teams in TPN order — there is
    // no equivalent of the individual `rrPairingOrder` pin to maintain.
    const field = teams.slice().sort((a, b) =>
        (parseInt(a.teamNo, 10) || 0) - (parseInt(b.teamNo, 10) || 0));

    const cycle = field.length % 2 === 0 ? field.length - 1 : field.length;
    const maxRounds = doubled ? cycle * 2 : cycle;
    if (roundNo > maxRounds) {
        // Two templates, not one with "round(s)": Norwegian inflects the noun too
        // ("1 runde", "3 runder"), so the number cannot simply be dropped into a plural.
        throw new Error(say(
            maxRounds === 1
                ? 'A round robin with {teams} teams runs for at most 1 round — every team has already met every other.'
                : 'A round robin with {teams} teams runs for at most {rounds} rounds — every team has already met every other.',
            { teams: field.length, rounds: maxRounds }
        ));
    }

    const raw = window.RoundRobinEngine.generatePairings(field, roundNo, maxRounds);

    const byes = [];
    const coloured = [];
    raw.forEach(pr => {
        // RoundRobinEngine marks the odd team out with the 'WO' sentinel, on either side.
        if (pr.black === 'WO') { byes.push(pr.white); return; }
        if (pr.white === 'WO') { byes.push(pr.black); return; }
        coloured.push({ whiteTeam: pr.white, blackTeam: pr.black });
    });

    return {
        ...buildMatches(coloured, byes, { teams, boards, roundNo, lineups, isAvailable }),
        warnings: [],
        colourRisks: []   // the table fixes the colours; there is nothing to diverge from
    };
}

/**
 * One round paired by the in-house C.04.6 bracket pairer (js/core/team-pairer.js).
 *
 * Used when the competition ranks teams by BOARD points. That is a scoring bbpPairings
 * cannot be given — a TRF round cell holds win, draw or loss, and a four-board match has
 * nine possible board-point outcomes — so there is no way to project the tournament onto
 * it at all. Everything after the pairing is identical to the engine path: colours under
 * art. 4.3, then the boards.
 */
function generateTeamBracketRound({ teams, rounds, settings, lineups, isAvailable }) {
    const TeamEngine = window.TeamEngine;
    const boards = parseInt(settings.boards, 10) || 4;
    const roundNo = rounds.length + 1;
    const totalRounds = parseInt(settings.roundsCount, 10) || 0;

    const entered = teams.filter(t => t.status !== false);
    if (entered.length < 2) {
        throw new Error(say('At least two teams must be entered to pair a round.'));
    }
    if (totalRounds > 0 && rounds.length >= totalRounds) {
        throw new Error(say('All {n} rounds have already been played.', { n: totalRounds }));
    }
    rounds.forEach(round => {
        (round.matches || []).forEach(m => {
            if (m.blackTeam === 'BYE') return;
            const played = (round.pairings || []).filter(p => p.matchId === m.id);
            if (played.length === 0 || played.some(p => !p.result)) {
                throw new Error(say(
                    'Round {n} still has unfinished games. Enter every result before pairing the next round.',
                    { n: round.roundNo }
                ));
            }
        });
    });

    const opts = {
        primaryScore: settings.primaryScore || 'match',
        matchPointSystem: settings.matchPointSystem,
        pointSystem: settings.pointSystem,
        boards,
        colourPrefType: settings.colourPrefType || 'A',
        roundNo,
        totalRounds
    };

    const { pairs, bye, stats, unpairable } = window.TeamPairer.pairRound(entered, rounds, opts);

    if (unpairable) {
        throw new Error(say(
            'No legal pairing exists for this round — the remaining teams have all played each other. Reduce the number of rounds, or pair this round by hand.'
        ));
    }

    const coloured = TeamEngine.assignRoundColours(pairs, stats, {
        colourPrefType: opts.colourPrefType,
        initialColour: settings.initialColour,
        primaryScore: opts.primaryScore,
        isFinalRound: totalRounds > 0 && roundNo === totalRounds
    });

    return {
        ...buildMatches(coloured, bye ? [bye] : [], { teams, boards, roundNo, lineups, isAvailable }),
        warnings: [],
        // Nothing to diverge from: this path never consults the individual Dutch colour
        // rules that colourRuleRisks exists to warn about.
        colourRisks: []
    };
}

export async function generateTeamRound({ teams, rounds, settings, lineups = {}, isAvailable, name }) {
    const TeamEngine = window.TeamEngine;
    const boards = parseInt(settings.boards, 10) || 4;
    const roundNo = rounds.length + 1;
    const totalRounds = parseInt(settings.roundsCount, 10) || 0;

    // A round robin never reaches the pairing engine — see generateTeamBergerRound.
    if (settings.pairingSystem === 'round-robin' || settings.pairingSystem === 'double-round-robin') {
        return generateTeamBergerRound({ teams, rounds, settings, lineups, isAvailable });
    }

    // Neither does a competition ranked by BOARD points: that scoring cannot be written
    // into a TRF at all, so it is paired in-house against C.04.6's own criteria. Match
    // points — FIDE's default, and the overwhelmingly common case — go to bbpPairings
    // below, which is endorsed and tested against upstream's own fixtures.
    if ((settings.primaryScore || 'match') === 'game') {
        return generateTeamBracketRound({ teams, rounds, settings, lineups, isAvailable });
    }

    // Throws with an actionable message for missing/duplicate pairing numbers, unfinished
    // results, an unresolved initial colour, or a game-point-primary event.
    const { trf, teamByNo, warnings } = window.TeamPairingTRF.build({
        teams,
        rounds,
        meta: {
            name,
            numRounds: totalRounds || roundNo,
            initialColour: settings.initialColour,
            matchPointSystem: settings.matchPointSystem,
            pointSystem: settings.pointSystem,
            primaryScore: settings.primaryScore,
            boards
        }
    });

    // Exposed for console inspection when diagnosing a reported pairing, mirroring what
    // the individual path does with window.lastTrfInput.
    window.lastTeamTrfInput = trf;

    const { exitCode, output, stderr } = await runDutch(trf);
    window.lastTeamTrfOutput = output;

    if (exitCode !== BbpExit.OK) {
        throw new Error(describeExit(exitCode, stderr));
    }

    const pairs = parsePairingList(output);
    if (pairs.length === 0) {
        throw new Error(say(
            'The pairing engine returned no matches. This usually means the team data is incomplete — check that every team has a pairing number and a result for each round played so far.'
        ));
    }

    // ---- 1. back to teams, colours dropped ---------------------------------
    const byeTeams = [];
    const plainPairs = [];
    for (const { whiteNo, blackNo } of pairs) {
        const a = teamByNo.get(whiteNo);
        if (!a) throw new Error(say('The engine returned an unknown team pairing number ({n}).', { n: whiteNo }));
        if (blackNo === null) { byeTeams.push(a); continue; }
        const b = teamByNo.get(blackNo);
        if (!b) throw new Error(say('The engine returned an unknown team pairing number ({n}).', { n: blackNo }));
        // Order within the pair is not kept: which of the two takes White is decided below
        // under the team rules, not by whichever side the engine happened to list first.
        plainPairs.push([a, b]);
    }

    // ---- 2. colours under C.04.6 art. 4.3 ----------------------------------
    const stats = TeamEngine.calculateTeamStats(teams, rounds, {
        pointSystem: settings.pointSystem,
        matchPointSystem: settings.matchPointSystem,
        boards
    });
    const coloured = TeamEngine.assignRoundColours(plainPairs, stats, {
        colourPrefType: settings.colourPrefType || 'A',
        initialColour: settings.initialColour,
        primaryScore: settings.primaryScore || 'match',
        isFinalRound: totalRounds > 0 && roundNo === totalRounds
    });

    // The one place driving bbpPairings with team data can legitimately diverge from
    // C.04.6 — it enforces the Dutch absolute colour rules, which do not apply to teams.
    // Reported rather than hidden; art. 4.3.5 normally keeps every team well clear of them.
    const colourRisks = TeamEngine.colourRuleRisks(teams, stats);

    // ---- 3. boards ---------------------------------------------------------
    const built = buildMatches(coloured, byeTeams, { teams, boards, roundNo, lineups, isAvailable });

    return { ...built, warnings, colourRisks };
}
